import React, { useContext, useEffect } from 'react';

import WindowContext from './WindowContext';

interface IdaasBridgeProps {
  /** Fired when the code is successfully verified. */
  onSuccess: () => void;
}

/**
 * This factor opens a popup window to initiate the IDaaS MFA flow, and listens
 * for messages from the popup conveying the result.
 */
export default function IdaasBridge({
  onSuccess: handleSuccess,
}: IdaasBridgeProps): React.ReactNode {
  // The initial render of this component represents the selection of IDaaS -
  // the subsequent renders are due to changes in `handleSuccess`, most likely
  // due to the MFA Prompt resetting its state at the end of the flow. We only
  // need to open the popup on initial render.
  const isInitialRender = React.useRef(true);

  const { open } = useContext(WindowContext);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_NAV_API ?? '';

    if (isInitialRender.current) {
      isInitialRender.current = false;

      const width = Math.floor(window.innerWidth * 0.95);
      const height = Math.floor(window.innerHeight * 0.9);
      const left =
        window.screenLeft + Math.floor((window.innerWidth - width) / 2);
      const top =
        window.screenTop + Math.floor((window.innerHeight - height) / 2);

      open(
        `${baseUrl}/ECMv6/api/authms/idaas/mfa/initiate`,
        'window:nav/mfa-prompt/idaas',
        `popup,width=${width},height=${height},top=${top},left=${left}`
      );
    }

    const handleMessage = (event: MessageEvent<{ isSuccess: boolean }>) => {
      if (event.data.isSuccess) {
        handleSuccess();
      }
    };

    const bc = new BroadcastChannel('channel:nav/mfa-prompt/idaas');

    bc.addEventListener('message', handleMessage);

    return () => {
      bc.removeEventListener('message', handleMessage);
      bc.close();
    };
  }, [handleSuccess, open]);

  return <>A popup has been opened to verify your identity...</>;
}
